(() => {
  const RUNTIME_KEY = '__ecResinV7RuntimeRegistered';
  if (window[RUNTIME_KEY]) return;
  window[RUNTIME_KEY] = true;

  const initialiseGallery = (gallery) => {
    if (gallery.dataset.ecGalleryReady === 'true') return;

    const slides = Array.from(gallery.querySelectorAll('[data-ec-rv7-slide]'));
    const thumbnails = Array.from(gallery.querySelectorAll('[data-ec-rv7-thumbnail]'));
    const previous = gallery.querySelector('[data-ec-rv7-previous]');
    const next = gallery.querySelector('[data-ec-rv7-next]');
    const current = gallery.querySelector('[data-ec-rv7-current]');
    const total = gallery.querySelector('[data-ec-rv7-total]');
    const caption = gallery.querySelector('[data-ec-rv7-caption]');

    if (!slides.length) return;

    let activeIndex = 0;

    const pauseSlideMedia = (slide) => {
      slide.querySelectorAll('video').forEach((video) => video.pause());
      slide.querySelectorAll('[data-ec-rv7-youtube]').forEach((frame) => frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*'));
      slide.querySelectorAll('[data-ec-rv7-vimeo]').forEach((frame) => frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*'));
    };

    const show = (requestedIndex, moveFocus = false) => {
      activeIndex = (requestedIndex + slides.length) % slides.length;

      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        if (!isActive) pauseSlideMedia(slide);
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      thumbnails.forEach((thumbnail, index) => {
        const isActive = index === activeIndex;
        thumbnail.classList.toggle('is-active', isActive);
        thumbnail.setAttribute('aria-current', isActive ? 'true' : 'false');
        if (isActive && moveFocus) thumbnail.focus({ preventScroll: true });
      });

      if (current) current.textContent = String(activeIndex + 1);
      if (total) total.textContent = String(slides.length);
      if (caption) caption.textContent = slides[activeIndex].dataset.caption || '';
    };

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => show(index));
    });

    previous?.addEventListener('click', () => show(activeIndex - 1));
    next?.addEventListener('click', () => show(activeIndex + 1));

    gallery.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        show(activeIndex - 1, true);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        show(activeIndex + 1, true);
      }
    });

    gallery.addEventListener('ec:show-media-block', (event) => {
      const targetIndex = slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
      if (targetIndex >= 0) show(targetIndex);
    });

    gallery.dataset.ecGalleryReady = 'true';
    show(0);
  };

  const initialiseAll = (root = document) => {
    root.querySelectorAll('[data-ec-rv7-gallery]').forEach(initialiseGallery);
  };

  const getRechargeRoots = (widget) => {
    const roots = [];

    const inspectRoot = (root) => {
      if (!root || roots.includes(root)) return;
      roots.push(root);
      root.querySelectorAll('*').forEach((element) => {
        if (element.shadowRoot) inspectRoot(element.shadowRoot);
      });
    };

    if (widget?.shadowRoot) inspectRoot(widget.shadowRoot);
    return roots;
  };

  const readRechargeSelection = (widget) => {
    const checkedPurchaseModes = new Set();
    const checkedSellingPlanIds = new Set();
    const availableSellingPlanIds = new Set();

    getRechargeRoots(widget).forEach((root) => {
      root.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (input.name === 'rc_plan' && input.value) availableSellingPlanIds.add(String(input.value));
        if (!input.checked) return;
        if (input.name === 'purchaseOption' && input.value) checkedPurchaseModes.add(String(input.value));
        if (input.name === 'rc_plan' && input.value) checkedSellingPlanIds.add(String(input.value));
      });
    });

    return {
      purchaseMode: checkedPurchaseModes.size === 1 ? [...checkedPurchaseModes][0] : null,
      sellingPlanId: checkedSellingPlanIds.size === 1 ? [...checkedSellingPlanIds][0] : null,
      availableSellingPlanIds: [...availableSellingPlanIds],
      hasAmbiguousPurchaseMode: checkedPurchaseModes.size > 1,
      hasAmbiguousSellingPlan: checkedSellingPlanIds.size > 1
    };
  };

  const formatMoney = (cents, currency) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(cents || 0) / 100);

  const cadenceLabel = (plan) => {
    const configuredMonths = Number(plan?.cadence_months || 0);
    if (configuredMonths > 0) return `every ${configuredMonths === 1 ? '' : `${configuredMonths} `}month${configuredMonths === 1 ? '' : 's'}`;
    const planName = String(plan?.cadence || '');
    const match = planName.match(/(\d+)\s*[- ]?month/i);
    if (!match && /monthly|every\s+month/i.test(planName)) return 'every month';
    if (!match) return 'at the selected delivery interval';
    const count = Number(match[1]);
    return `every ${count} ${count === 1 ? 'month' : 'months'}`;
  };

  const capitalise = (value) => value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : '';

  const setText = (element, text) => {
    if (element && element.textContent !== text) element.textContent = text;
  };

  const copyValue = (planData, key, fallback) => {
    if (!planData?.copy || !Object.prototype.hasOwnProperty.call(planData.copy, key)) return fallback;
    return String(planData.copy[key] ?? '');
  };

  const interpolateCopy = (template, values) => Object.entries(values).reduce(
    (result, [key, value]) => result
      .split(`{{${key}}}`).join(String(value))
      .split(`[${key}]`).join(String(value)),
    String(template || '')
  );

  const associatedLabel = (input) => {
    const root = input?.getRootNode?.();
    if (input?.id && root?.querySelector) {
      const escapedId = window.CSS?.escape ? window.CSS.escape(input.id) : input.id.replace(/[^a-zA-Z0-9_-]/g, '');
      const explicitLabel = root.querySelector(`label[for="${escapedId}"]`);
      if (explicitLabel) return explicitLabel;
    }
    return input?.closest?.('label') || input?.parentElement || null;
  };

  const rechargeSkin = `
    :host {
      --ec-rv7-recharge-ink: #171714;
      --ec-rv7-recharge-green: #3e5b46;
      --ec-rv7-recharge-green-soft: #edf1ea;
      --ec-rv7-recharge-line: #d9d2c5;
      --ec-rv7-recharge-paper: #fffef8;
      display: block;
      container-type: inline-size;
    }
    .rc-widget__root {
      color: var(--ec-rv7-recharge-ink) !important;
      font-family: inherit !important;
    }
    .rc-purchase-option-group {
      min-width: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
    }
    .rc-selection__root {
      display: grid !important;
      gap: 12px !important;
    }
    .rc-purchase-option {
      min-width: 0 !important;
      border: 1px solid var(--ec-rv7-recharge-line) !important;
      border-radius: 14px !important;
      background: var(--ec-rv7-recharge-paper) !important;
      box-sizing: border-box !important;
    }
    .rc-purchase-option__subscription {
      position: relative !important;
      padding: 14px !important;
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) minmax(122px, .72fr) !important;
      gap: 13px 16px !important;
      border-color: var(--ec-rv7-recharge-green) !important;
      background: linear-gradient(135deg, #fffef8 0%, #f4f7f1 100%) !important;
    }
    .rc-purchase-option[data-ec-rv7-selected="true"] {
      border-color: var(--ec-rv7-recharge-green) !important;
      box-shadow: inset 0 0 0 1px var(--ec-rv7-recharge-green) !important;
    }
    .rc-purchase-option__subscription > .rc-purchase-option__badge,
    .rc-purchase-option__subscription > .rc-purchase-option__dynamic-price,
    .rc-purchase-option__subscription rc-benefits,
    .rc-purchase-option__subscription rc-learn-more {
      display: none !important;
    }
    .rc-purchase-option__subscription > .rc-purchase-option__label {
      min-width: 0 !important;
      padding: 2px 0 !important;
      display: flex !important;
      flex-direction: column !important;
      align-items: flex-start !important;
      justify-content: center !important;
      gap: 5px !important;
      border: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
    }
    .rc-purchase-option__subscription .rc-purchase-option__selector {
      width: 100% !important;
      display: flex !important;
      align-items: center !important;
      gap: 8px !important;
      color: var(--ec-rv7-recharge-ink) !important;
      font-size: 17px !important;
      font-weight: 780 !important;
      line-height: 1.15 !important;
    }
    .rc-purchase-option__subscription .rc-purchase-option__prices {
      display: none !important;
    }
    .ec-rv7-recharge-subtitle {
      display: block;
      padding-left: 31px;
      color: #5c5a54;
      font-size: 10px;
      line-height: 1.35;
    }
    .ec-rv7-recharge-offer-badge {
      position: absolute;
      top: 12px;
      right: 12px;
      z-index: 2;
      padding: 6px 9px;
      border-radius: 999px;
      background: #e6eee4;
      color: var(--ec-rv7-recharge-green);
      font-size: 7px;
      font-weight: 850;
      letter-spacing: .06em;
      line-height: 1;
      text-transform: uppercase;
    }
    .ec-rv7-recharge-today {
      min-width: 0;
      padding: 12px 13px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      border-left: 1px solid var(--ec-rv7-recharge-line);
      color: var(--ec-rv7-recharge-ink);
    }
    .ec-rv7-recharge-today-label {
      margin-bottom: 7px;
      color: #7a5c2b;
      font-size: 7px;
      font-weight: 850;
      letter-spacing: .12em;
      line-height: 1;
      text-transform: uppercase;
    }
    .ec-rv7-recharge-today-prices {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 5px 8px;
    }
    .ec-rv7-recharge-today-price {
      color: inherit;
      font-size: 24px;
      font-weight: 820;
      letter-spacing: -.035em;
      line-height: 1;
    }
    .ec-rv7-recharge-today-compare {
      color: #77746d;
      font-size: 11px;
    }
    .rc-purchase-option__subscription > .rc-purchase-option__sub-container {
      grid-column: 1 / -1 !important;
      min-width: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      display: block !important;
    }
    .rc-purchase-option__subscription rc-selling-plans {
      width: 100% !important;
      min-width: 0 !important;
      display: block !important;
    }
    .rc-selection__onetime {
      padding: 0 !important;
      overflow: hidden !important;
    }
    .rc-selection__onetime > .rc-purchase-option__label {
      width: 100% !important;
      max-width: 100% !important;
      min-height: 62px !important;
      padding: 12px 14px !important;
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) auto !important;
      align-items: center !important;
      gap: 5px 12px !important;
      border: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      box-sizing: border-box !important;
    }
    .rc-selection__onetime .rc-purchase-option__selector {
      min-width: 0 !important;
      display: flex !important;
      align-items: center !important;
      gap: 8px !important;
      font-size: 15px !important;
      font-weight: 760 !important;
    }
    .rc-selection__onetime .rc-purchase-option__prices {
      font-size: 16px !important;
      font-weight: 800 !important;
    }
    .ec-rv7-recharge-onetime-subtitle {
      grid-column: 1 / -1;
      padding-left: 31px;
      color: #666159;
      font-size: 9px;
      line-height: 1.25;
    }
    .ec-rv7__purchase-benefits[data-ec-rv7-recharge-assurances="true"] {
      grid-column: 1 / -1 !important;
      width: 100% !important;
      min-width: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      display: grid !important;
      grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
      overflow: hidden !important;
      border: 1px solid var(--ec-rv7-recharge-line) !important;
      border-radius: 11px !important;
      background: rgba(240, 243, 237, .82) !important;
      list-style: none !important;
      box-sizing: border-box !important;
    }
    .ec-rv7__purchase-benefits[data-ec-rv7-recharge-assurances="true"] li {
      min-width: 0 !important;
      min-height: 58px !important;
      padding: 9px 10px !important;
      display: grid !important;
      grid-template-columns: 28px minmax(0, 1fr) !important;
      align-items: center !important;
      gap: 8px !important;
      color: #415146 !important;
      font-size: 9px !important;
      line-height: 1.22 !important;
      text-align: left !important;
      box-sizing: border-box !important;
    }
    .ec-rv7__purchase-benefits[data-ec-rv7-recharge-assurances="true"] li + li {
      border-left: 1px solid #d6d7ce !important;
    }
    .ec-rv7__purchase-benefits[data-ec-rv7-recharge-assurances="true"] li > span:last-child {
      min-width: 0 !important;
      display: flex !important;
      flex-direction: column !important;
      gap: 2px !important;
    }
    .ec-rv7__purchase-benefits[data-ec-rv7-recharge-assurances="true"] strong {
      color: #24382b !important;
      font-size: 10px !important;
      font-weight: 760 !important;
      line-height: 1.16 !important;
    }
    .ec-rv7__purchase-benefits[data-ec-rv7-recharge-assurances="true"] small {
      color: #687269 !important;
      font-size: 7px !important;
      line-height: 1.22 !important;
    }
    .ec-rv7__purchase-benefits[data-ec-rv7-recharge-assurances="true"] .ec-rv7__assurance-icon {
      width: 28px !important;
      height: 28px !important;
      display: grid !important;
      place-items: center !important;
      border: 1px solid #b9c5ba !important;
      border-radius: 50% !important;
      background: rgba(255, 255, 255, .72) !important;
      box-sizing: border-box !important;
    }
    .ec-rv7__purchase-benefits[data-ec-rv7-recharge-assurances="true"] .ec-rv7__assurance-icon svg {
      width: 15px !important;
      height: 15px !important;
    }
    [data-ec-rv7-purchase-card="true"],
    [data-ec-rv7-plan-card="true"] {
      border-radius: 12px !important;
      transition: border-color .18s ease, background-color .18s ease, box-shadow .18s ease;
    }
    [data-ec-rv7-plan-card="true"] {
      position: relative !important;
      display: block !important;
      flex: 1 1 0 !important;
      min-width: 94px !important;
      width: auto !important;
      overflow: hidden !important;
      border: 1px solid var(--ec-rv7-recharge-line) !important;
      background: var(--ec-rv7-recharge-paper) !important;
      box-sizing: border-box !important;
    }
    [data-ec-rv7-purchase-card="true"][data-ec-rv7-selected="true"],
    [data-ec-rv7-plan-card="true"][data-ec-rv7-selected="true"] {
      border-color: var(--ec-rv7-recharge-green) !important;
      background: var(--ec-rv7-recharge-green-soft) !important;
      box-shadow: inset 0 0 0 1px var(--ec-rv7-recharge-green) !important;
    }
    [part~="rc-plans-button-group"] {
      width: 100% !important;
      min-width: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      display: flex !important;
      flex-flow: row wrap !important;
      align-items: stretch !important;
      gap: 8px !important;
      border: 0 !important;
      box-sizing: border-box !important;
    }
    [part~="rc-plans__label"] {
      flex: 0 0 100% !important;
      width: 100% !important;
      margin: 0 0 2px !important;
      padding: 0 !important;
      color: #5c5a54 !important;
      font-size: 9px !important;
      font-weight: 780 !important;
      letter-spacing: .09em !important;
      line-height: 1.2 !important;
      text-transform: uppercase !important;
    }
    [data-ec-rv7-plan-card="true"] .rc-plans-button {
      width: 100% !important;
      min-width: 0 !important;
      min-height: 104px !important;
      height: 100% !important;
      padding: 13px 9px 10px !important;
      display: grid !important;
      grid-template-columns: 16px minmax(0, 1fr) !important;
      grid-template-areas:
        "radio interval"
        "price price"
        "discount discount" !important;
      align-items: flex-start !important;
      align-content: center !important;
      gap: 4px 6px !important;
      overflow: hidden !important;
      border: 0 !important;
      border-radius: 0 !important;
      background: transparent !important;
      color: var(--ec-rv7-recharge-ink) !important;
      box-sizing: border-box !important;
      text-align: left !important;
    }
    [data-ec-rv7-best-plan="true"] .rc-plans-button {
      padding-top: 29px !important;
    }
    [data-ec-rv7-plan-card="true"] .rc-plans-button__interval {
      grid-area: interval;
      max-width: 100% !important;
      color: var(--ec-rv7-recharge-ink) !important;
      font-size: 12px !important;
      font-weight: 800 !important;
      line-height: 1.12 !important;
      white-space: normal !important;
    }
    [data-ec-rv7-plan-card="true"] .rc-plans-button__discount {
      grid-area: discount;
      max-width: 100% !important;
      color: var(--ec-rv7-recharge-green) !important;
      font-size: 9px !important;
      font-weight: 650 !important;
      line-height: 1.2 !important;
      white-space: normal !important;
    }
    .ec-rv7-plan-radio {
      grid-area: radio;
      width: 15px;
      height: 15px;
      display: grid;
      place-items: center;
      border: 1px solid #9d927d;
      border-radius: 50%;
      background: #fffef8;
      box-sizing: border-box;
    }
    [data-ec-rv7-selected="true"] .ec-rv7-plan-radio {
      border-color: var(--ec-rv7-recharge-green);
    }
    [data-ec-rv7-selected="true"] .ec-rv7-plan-radio::after {
      content: "";
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: var(--ec-rv7-recharge-green);
    }
    .ec-rv7-plan-price {
      grid-area: price;
      display: block;
      color: var(--ec-rv7-recharge-ink);
      font-size: 15px;
      font-weight: 820;
      letter-spacing: -.02em;
      line-height: 1.05;
    }
    .ec-rv7-plan-renewal {
      display: block;
      width: 100%;
      max-width: 100%;
      margin-top: 4px;
      color: #415146;
      font-size: 9px;
      font-weight: 700;
      line-height: 1.22;
      overflow-wrap: break-word;
      box-sizing: border-box;
    }
    .ec-rv7-plan-badge {
      position: absolute;
      top: 5px;
      right: 5px;
      z-index: 2;
      display: inline-flex;
      width: auto;
      max-width: 100%;
      margin: 0 0 4px;
      padding: 4px 6px;
      border-radius: 999px;
      background: var(--ec-rv7-recharge-green);
      color: #fff;
      font-size: 7px;
      font-weight: 800;
      letter-spacing: .06em;
      line-height: 1.08;
      text-align: center;
      text-transform: uppercase;
      white-space: normal;
      box-sizing: border-box;
    }
    @container (max-width: 559px) {
      .rc-purchase-option__subscription {
        grid-template-columns: 1fr !important;
        gap: 12px !important;
      }
      .rc-purchase-option__subscription > .rc-purchase-option__label {
        padding-right: 78px !important;
      }
      .ec-rv7-recharge-today {
        min-height: 112px;
        padding: 18px 16px;
        border: 0;
        border-radius: 12px;
        background: #191917;
        color: #fff;
      }
      .ec-rv7-recharge-today-label {
        color: #d5b766;
        font-size: 8px;
      }
      .ec-rv7-recharge-today-price {
        font-size: 34px;
      }
      .ec-rv7-recharge-today-compare {
        color: #a9a69f;
        font-size: 16px;
      }
      [part~="rc-plans-button-group"] {
        flex-direction: column !important;
        gap: 8px !important;
      }
      [data-ec-rv7-plan-card="true"] {
        flex: 0 0 auto !important;
        min-width: 0 !important;
        width: 100% !important;
      }
      [data-ec-rv7-plan-card="true"] .rc-plans-button {
        min-height: 76px !important;
        padding: 12px 13px !important;
        grid-template-columns: 18px minmax(0, 1fr) auto !important;
        grid-template-areas:
          "radio interval price"
          "radio discount price" !important;
        align-items: center !important;
        gap: 3px 9px !important;
      }
      [data-ec-rv7-best-plan="true"] .rc-plans-button {
        min-height: 90px !important;
        padding-top: 27px !important;
      }
      [data-ec-rv7-plan-card="true"] .rc-plans-button__interval {
        font-size: 14px !important;
      }
      .ec-rv7-plan-price {
        font-size: 16px;
      }
      .ec-rv7-plan-badge {
        top: 4px;
        right: 5px;
        padding: 4px 6px;
        font-size: 6px;
      }
      .ec-rv7__purchase-benefits[data-ec-rv7-recharge-assurances="true"] {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      }
      .ec-rv7__purchase-benefits[data-ec-rv7-recharge-assurances="true"] li:nth-child(odd) {
        border-left: 0 !important;
      }
      .ec-rv7__purchase-benefits[data-ec-rv7-recharge-assurances="true"] li:nth-child(n + 3) {
        border-top: 1px solid #d6d7ce !important;
      }
    }
    @container (min-width: 900px) {
      .rc-purchase-option__subscription {
        grid-template-columns: minmax(230px, 1fr) minmax(170px, .72fr) minmax(450px, 1.9fr) !important;
        align-items: stretch !important;
        gap: 0 !important;
      }
      .rc-purchase-option__subscription > .rc-purchase-option__label {
        padding: 14px 18px !important;
      }
      .ec-rv7-recharge-today {
        padding: 14px 24px !important;
      }
      .rc-purchase-option__subscription > .rc-purchase-option__sub-container {
        grid-column: auto !important;
        padding: 12px 0 12px 26px !important;
        border-left: 1px solid var(--ec-rv7-recharge-line) !important;
      }
      .rc-purchase-option__subscription > .ec-rv7__purchase-benefits[data-ec-rv7-recharge-assurances="true"] {
        grid-column: 1 / -1 !important;
        margin-top: 14px !important;
      }
      [data-ec-rv7-plan-card="true"] .rc-plans-button {
        min-height: 112px !important;
      }
    }
  `;

  const enhanceRechargeWidget = (widget, planData) => {
    const roots = getRechargeRoots(widget);
    let enhancedPurchaseCard = false;
    let enhancedPlanCount = 0;
    const plansById = new Map(planData.plans.map((plan) => [String(plan.id), plan]));
    const visiblePlanIds = new Set();
    let selectedPlanId = null;
    roots.forEach((root) => {
      root.querySelectorAll('input[type="radio"][name="rc_plan"]').forEach((input) => visiblePlanIds.add(String(input.value)));
      const checkedPlan = root.querySelector('input[type="radio"][name="rc_plan"]:checked');
      if (checkedPlan) selectedPlanId = String(checkedPlan.value);
    });
    const visiblePlans = planData.plans.filter((plan) => !visiblePlanIds.size || visiblePlanIds.has(String(plan.id)));
    const selectedPlan = plansById.get(String(selectedPlanId)) || visiblePlans[0];
    const bestPlan = visiblePlans.reduce((best, plan) => {
      const bestDiscount = Number(best?.recurring_discount || 0);
      const currentDiscount = Number(plan?.recurring_discount || 0);
      return currentDiscount > bestDiscount ? plan : best;
    }, visiblePlans[0]);

    roots.forEach((root) => {
      if (!root.querySelector('style[data-ec-rv7-recharge-skin]')) {
        const style = document.createElement('style');
        style.dataset.ecRv7RechargeSkin = 'true';
        style.textContent = rechargeSkin;
        root.appendChild(style);
      }

      root.querySelectorAll('input[type="radio"][name="purchaseOption"]').forEach((input) => {
        const label = associatedLabel(input);
        if (!label) return;
        label.dataset.ecRv7PurchaseCard = 'true';
        label.dataset.ecRv7Selected = input.checked ? 'true' : 'false';
        const purchaseCard = label.closest('.rc-purchase-option');
        if (purchaseCard) purchaseCard.dataset.ecRv7Selected = input.checked ? 'true' : 'false';

        if (input.value === 'subscription') {
          const subscriptionSubtitle = copyValue(planData, 'subscription_subtitle', 'Free U.S. shipping · manage through Recharge');
          let subtitle = label.querySelector('.ec-rv7-recharge-subtitle');
          if (subscriptionSubtitle && !subtitle) {
            subtitle = document.createElement('span');
            subtitle.className = 'ec-rv7-recharge-subtitle';
            label.appendChild(subtitle);
          }
          if (subscriptionSubtitle) setText(subtitle, subscriptionSubtitle);
          else subtitle?.remove();
        }

        if (input.value === 'onetime') {
          const oneTimeLabel = copyValue(planData, 'one_time_label', 'One-time purchase');
          const selector = label.querySelector('.rc-purchase-option__selector');
          const titleNode = selector
            ? Array.from(selector.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && /one-time/i.test(node.textContent || ''))
            : null;
          if (titleNode && oneTimeLabel && titleNode.textContent.trim() !== oneTimeLabel) {
            titleNode.textContent = ` ${oneTimeLabel}`;
          }

          let subtitle = label.querySelector('.ec-rv7-recharge-onetime-subtitle');
          if (!subtitle) {
            subtitle = document.createElement('span');
            subtitle.className = 'ec-rv7-recharge-onetime-subtitle';
            label.appendChild(subtitle);
          }
          setText(subtitle, planData.one_time_description || 'One item · no subscription');
        }
      });

      const subscriptionCard = root.querySelector('.rc-purchase-option__subscription');
      if (subscriptionCard && selectedPlan) {
        enhancedPurchaseCard = true;
        const assurances = widget.__ecRv7Assurances;
        if (assurances) {
          assurances.dataset.ecRv7RechargeAssurances = 'true';
          if (assurances.parentElement !== subscriptionCard) subscriptionCard.appendChild(assurances);
        }
        const offerBadgeCopy = copyValue(planData, 'offer_badge', 'Best value');
        let offerBadge = subscriptionCard.querySelector('.ec-rv7-recharge-offer-badge');
        if (offerBadgeCopy && !offerBadge) {
          offerBadge = document.createElement('span');
          offerBadge.className = 'ec-rv7-recharge-offer-badge';
          subscriptionCard.appendChild(offerBadge);
        }
        if (offerBadgeCopy) setText(offerBadge, offerBadgeCopy);
        else offerBadge?.remove();

        let todayPanel = subscriptionCard.querySelector('.ec-rv7-recharge-today');
        if (!todayPanel) {
          todayPanel = document.createElement('div');
          todayPanel.className = 'ec-rv7-recharge-today';

          const todayLabel = document.createElement('span');
          todayLabel.className = 'ec-rv7-recharge-today-label';
          todayLabel.textContent = copyValue(planData, 'today_label', 'You pay today');

          const priceRow = document.createElement('div');
          priceRow.className = 'ec-rv7-recharge-today-prices';

          const todayPrice = document.createElement('strong');
          todayPrice.className = 'ec-rv7-recharge-today-price';

          const todayCompare = document.createElement('s');
          todayCompare.className = 'ec-rv7-recharge-today-compare';

          priceRow.append(todayPrice, todayCompare);
          todayPanel.append(todayLabel, priceRow);
        }

        const planContainer = subscriptionCard.querySelector(':scope > .rc-purchase-option__sub-container');
        if (planContainer) subscriptionCard.insertBefore(todayPanel, planContainer);
        else if (!todayPanel.parentElement) subscriptionCard.appendChild(todayPanel);

        setText(todayPanel.querySelector('.ec-rv7-recharge-today-price'), formatMoney(selectedPlan.first_price, planData.currency));
        setText(todayPanel.querySelector('.ec-rv7-recharge-today-compare'), formatMoney(planData.one_time_price, planData.currency));
      }

      root.querySelectorAll('input[type="radio"][name="rc_plan"]').forEach((input) => {
        const plan = plansById.get(String(input.value));
        const label = associatedLabel(input);
        if (!plan || !label) return;
        enhancedPlanCount += 1;

        label.dataset.ecRv7PlanCard = 'true';
        label.dataset.ecRv7Selected = input.checked ? 'true' : 'false';

        // Recharge's label is a horizontal flex row. Keep all enhanced copy
        // inside its existing visual button so no extra flex child can collide
        // with a neighbouring cadence card.
        const planButton = label.querySelector('.rc-plans-button') || label;
        const recurringDiscount = Number(plan.recurring_discount || 0);

        label.querySelector('.ec-rv7-plan-renewal')?.remove();

        let radio = planButton.querySelector('.ec-rv7-plan-radio');
        if (!radio) {
          radio = document.createElement('span');
          radio.className = 'ec-rv7-plan-radio';
          radio.setAttribute('aria-hidden', 'true');
          planButton.prepend(radio);
        }

        const interval = planButton.querySelector('.rc-plans-button__interval');
        const discount = planButton.querySelector('.rc-plans-button__discount');
        setText(interval, capitalise(cadenceLabel(plan)));
        const discountSuffix = copyValue(planData, 'renewal_discount_suffix', 'off renewal');
        const standardRenewal = copyValue(planData, 'standard_renewal_label', 'Standard renewal price');
        setText(discount, recurringDiscount > 0 ? `${recurringDiscount}% ${discountSuffix}`.trim() : standardRenewal);

        let price = planButton.querySelector('.ec-rv7-plan-price');
        if (!price) {
          price = document.createElement('strong');
          price.className = 'ec-rv7-plan-price';
          planButton.appendChild(price);
        }
        setText(price, formatMoney(plan.recurring_price, planData.currency));

        const isBestPlan = String(plan.id) === String(bestPlan?.id);
        label.dataset.ecRv7BestPlan = isBestPlan ? 'true' : 'false';
        const bestPlanBadge = copyValue(planData, 'best_plan_badge', 'Best renewal value');
        let badge = label.querySelector('.ec-rv7-plan-badge');
        if (isBestPlan && bestPlanBadge && !badge) {
          badge = document.createElement('span');
          badge.className = 'ec-rv7-plan-badge';
          badge.textContent = bestPlanBadge;
          label.appendChild(badge);
        } else if (isBestPlan && bestPlanBadge && badge && badge.parentElement !== label) {
          label.appendChild(badge);
        } else if ((!isBestPlan || !bestPlanBadge) && badge) {
          badge.remove();
        }
      });
    });

    return enhancedPurchaseCard && enhancedPlanCount > 0;
  };

  const initialiseSubscriptionTruth = (section) => {
    if (section.dataset.ecSubscriptionTruthReady === 'true') return;
    if (section.dataset.ecSubscriptionTruthBooting === 'true') return;

    const dataElement = section.querySelector('[data-ec-rv7-plan-data]');
    const summary = section.querySelector('[data-ec-rv7-subscription-truth]');
    const todayLabel = section.querySelector('[data-ec-rv7-subscription-today-label]');
    const today = section.querySelector('[data-ec-rv7-subscription-today]');
    const compare = section.querySelector('[data-ec-rv7-subscription-compare]');
    const renewalLabel = section.querySelector('[data-ec-rv7-subscription-renewal-label]');
    const next = section.querySelector('[data-ec-rv7-subscription-next]');
    const cadence = section.querySelector('[data-ec-rv7-subscription-cadence]');
    const unit = section.querySelector('[data-ec-rv7-subscription-unit]');
    const disclosure = section.querySelector('[data-ec-rv7-subscription-disclosure]');
    const purchaseNote = section.querySelector('[data-ec-rv7-purchase-note]');
    const assurances = section.querySelector('.ec-rv7__purchase-benefits');
    const widget = section.querySelector('recharge-subscription-widget');
    if (!dataElement || !summary || !todayLabel || !today || !compare || !renewalLabel || !next || !cadence || !unit || !disclosure || !purchaseNote || !widget) return;
    widget.__ecRv7Assurances = assurances;

    let planData;
    try {
      planData = JSON.parse(dataElement.textContent || '{}');
    } catch (_) {
      return;
    }
    if (!Array.isArray(planData.plans) || !planData.plans.length) return;

    // Unit value is published only when the section provides a verified
    // customer-facing divisor. Otherwise this fails closed and renders nothing.
    const unitValue = (price) => {
      const divisor = Number(planData.unit_divisor);
      if (!Number.isFinite(divisor) || divisor <= 0 || !planData.unit_label) return '';
      return `${formatMoney(Number(price) / divisor, planData.currency)} ${planData.unit_label}`;
    };

    const setUnitText = (text) => {
      unit.textContent = text;
      unit.hidden = !text;
    };

    const setAddButtonText = (text) => {
      const addButton = section.querySelector('.ec-rv7__add-button:not([disabled])');
      if (addButton) addButton.textContent = text;
    };

    const refresh = () => {
      const rechargeEnhanced = enhanceRechargeWidget(widget, planData);
      section.dataset.ecRechargeOfferEnhanced = rechargeEnhanced ? 'true' : 'false';
      const selection = readRechargeSelection(widget);
      if (!selection.purchaseMode || selection.hasAmbiguousPurchaseMode) {
        summary.hidden = true;
        return;
      }
      if (selection.purchaseMode === 'onetime') {
        summary.classList.add('is-one-time');
        todayLabel.textContent = copyValue(planData, 'one_time_total_label', 'ONE-TIME TOTAL');
        today.textContent = formatMoney(planData.one_time_price, planData.currency);
        compare.textContent = '';
        compare.hidden = true;
        renewalLabel.textContent = copyValue(planData, 'purchase_type_label', 'PURCHASE TYPE');
        next.textContent = planData.one_time_description || 'One item · no subscription';
        cadence.textContent = copyValue(planData, 'no_recurring_label', 'No recurring charge');
        setUnitText(unitValue(planData.one_time_price));
        disclosure.textContent = copyValue(planData, 'one_time_disclosure', 'One-time purchase. No recurring charge.');
        purchaseNote.textContent = disclosure.textContent;
        setAddButtonText(interpolateCopy(
          copyValue(planData, 'one_time_button_template', 'Add one-time purchase · [price]'),
          { price: formatMoney(planData.one_time_price, planData.currency) }
        ));
        summary.hidden = false;
        return;
      }

      const selectedPlan = planData.plans.find((plan) => String(plan.id) === String(selection.sellingPlanId));
      if (!selectedPlan || selection.hasAmbiguousSellingPlan) {
        summary.hidden = true;
        return;
      }
      const selectedCadence = cadenceLabel(selectedPlan);
      const recurringDiscount = Number(selectedPlan.recurring_discount || 0);
      summary.classList.remove('is-one-time');
      todayLabel.textContent = copyValue(planData, 'today_label', 'YOU PAY TODAY');
      today.textContent = formatMoney(selectedPlan.first_price, planData.currency);
      compare.textContent = formatMoney(planData.one_time_price, planData.currency);
      compare.hidden = false;
      renewalLabel.textContent = copyValue(planData, 'renewal_label', 'SELECTED RENEWAL');
      next.textContent = formatMoney(selectedPlan.recurring_price, planData.currency);
      const renewalDiscountSuffix = copyValue(planData, 'renewal_discount_suffix', 'off renewal');
      cadence.textContent = `${capitalise(selectedCadence)}${recurringDiscount > 0 ? ` · ${recurringDiscount}% ${renewalDiscountSuffix}` : ''}`.trim();
      const firstUnit = unitValue(selectedPlan.first_price);
      const recurringUnit = unitValue(selectedPlan.recurring_price);
      const firstUnitSuffix = copyValue(planData, 'unit_first_suffix', 'on your first order');
      const renewalUnitSuffix = copyValue(planData, 'unit_renewal_suffix', 'on renewals');
      setUnitText(firstUnit && recurringUnit ? `${firstUnit} ${firstUnitSuffix} · ${recurringUnit} ${renewalUnitSuffix}`.trim() : '');
      disclosure.textContent = interpolateCopy(
        copyValue(planData, 'renewal_disclosure_template', 'Renews at [price] [cadence] unless changed or cancelled.'),
        {
          price: formatMoney(selectedPlan.recurring_price, planData.currency),
          cadence: selectedCadence
        }
      );
      purchaseNote.textContent = disclosure.textContent;
      setAddButtonText(interpolateCopy(
        copyValue(planData, 'subscription_button_template', 'Start subscription · [price] today'),
        {
          price: formatMoney(selectedPlan.first_price, planData.currency),
          cadence: selectedCadence
        }
      ));
      summary.hidden = false;
    };

    const safeRefresh = () => {
      try {
        refresh();
        section.dataset.ecRechargeLoadState = 'ready';
      } catch (error) {
        section.dataset.ecRechargeLoadState = 'error';
        console.error('Essencraft Recharge enhancement failed.', error);
      }
    };

    const attachRootListener = (root) => {
      if (!root) return;
      if (!root.__ecSubscriptionTruthObserved) {
        root.__ecSubscriptionTruthObserved = true;
        root.addEventListener('change', safeRefresh);
      }
    };

    // Recharge creates several nested custom elements and open shadow roots.
    // A shadow root can exist before its purchase and cadence controls do, and
    // attaching a nested shadow root does not trigger the parent observer. Poll
    // until the actual controls exist so editor/section reloads cannot strand
    // the widget in its loading state.
    section.dataset.ecSubscriptionTruthBooting = 'true';
    section.dataset.ecRechargeLoadState = 'loading';
    let attempts = 0;
    const maxAttempts = 200;
    const waitForRechargeControls = window.setInterval(() => {
      attempts += 1;
      const roots = getRechargeRoots(widget);
      const hasPurchaseControls = roots.some((root) => root.querySelector('input[type="radio"][name="purchaseOption"]'));
      const hasPlanControls = roots.some((root) => root.querySelector('input[type="radio"][name="rc_plan"]'));

      if (hasPurchaseControls && hasPlanControls) {
        window.clearInterval(waitForRechargeControls);
        roots.forEach(attachRootListener);
        section.dataset.ecSubscriptionTruthReady = 'true';
        delete section.dataset.ecSubscriptionTruthBooting;
        safeRefresh();

        // Recharge can add nested roots briefly after its controls first appear.
        // Attach listeners to those roots without repeatedly rewriting Recharge's
        // live selector while the customer is choosing a cadence.
        let settleAttempts = 0;
        const settleRecharge = window.setInterval(() => {
          settleAttempts += 1;
          getRechargeRoots(widget).forEach(attachRootListener);
          if (settleAttempts >= 20) window.clearInterval(settleRecharge);
        }, 250);
        return;
      }

      if (attempts >= maxAttempts) {
        window.clearInterval(waitForRechargeControls);
        delete section.dataset.ecSubscriptionTruthBooting;
        section.dataset.ecRechargeLoadState = 'timeout';
        console.error('Essencraft Recharge widget timed out before purchase controls became available.');
      }
    }, 100);
  };

  const initialiseSubscriptionTruthAll = (root = document) => {
    root.querySelectorAll('[data-ec-rv7-section]').forEach(initialiseSubscriptionTruth);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialiseAll();
      initialiseSubscriptionTruthAll();
    });
  } else {
    initialiseAll();
    initialiseSubscriptionTruthAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialiseAll(event.target);
    initialiseSubscriptionTruthAll(event.target);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest('[data-ec-rv7-section]');
    const gallery = section?.querySelector('[data-ec-rv7-gallery]');
    if (!gallery) return;
    gallery.dispatchEvent(new CustomEvent('ec:show-media-block', {
      detail: { blockId: event.detail.blockId }
    }));
  });
})();
